// src/components/dashboard/TasksSummary.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Clock3, AlertTriangle, MapPin } from "lucide-react";
import { TaskService } from "../tasks/TaskService";
import { notifyTaskReminder } from "../../utils/notifications";

const roleLower = (role) => String(role || "").trim().toLowerCase();
const doneStatuses = new Set(["completed", "done", "closed", "cancelled"]);
const internalRoles = new Set(["owner", "admin", "enduser"]);

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDueDate = (value) => {
  const date = toDate(value);
  if (!date) return "No due date";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

function getCreatorIds(task) {
  return [task?.createdByUid, task?.createdBy]
    .filter(Boolean)
    .map((id) => String(id));
}

function TasksSummary({ companyId, me }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState("all");
  const [selectedTaskFilter, setSelectedTaskFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [notifyTaskId, setNotifyTaskId] = useState(null);

  const isAdmin = ["owner", "admin"].includes(roleLower(me?.role));
  const myIds = useMemo(
    () => Array.from(new Set([me?.uid, me?.id].filter(Boolean).map((id) => String(id)))),
    [me?.id, me?.uid]
  );

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const taskRows = await TaskService.getCompanyTasks(companyId, { me });
        let userRows = [];

        if (isAdmin) {
          try {
            userRows = await TaskService.getCompanyUsers(companyId);
          } catch (userError) {
            console.warn("[TasksSummary] Could not load company users:", userError);
          }
        }

        if (cancelled) return;
        setTasks(Array.isArray(taskRows) ? taskRows : []);
        setUsers(Array.isArray(userRows) ? userRows : []);
      } catch (error) {
        console.error("[TasksSummary] Error loading tasks:", error);
        if (!cancelled) {
          setTasks([]);
          setUsers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => {
      cancelled = true;
    };
  }, [companyId, isAdmin, me]);

  const assigneeOptions = useMemo(() => {
    const map = new Map();
    const allowedUserIds = new Set();

    (users || []).forEach((user) => {
      const uid = user?.uid || user?.id;
      if (!uid) return;
      if (!internalRoles.has(roleLower(user?.role))) return;
      allowedUserIds.add(String(uid));
      map.set(String(uid), user?.fullName || user?.displayName || user?.email || "Team member");
    });

    (tasks || []).forEach((task) => {
      const ids = [
        ...(Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds : []),
        ...(Array.isArray(task?.memberIds) ? task.memberIds : []),
        task?.assignedUserId,
        task?.assignedToId,
        task?.assignedTo,
      ].filter(Boolean);

      ids.forEach((id) => {
        const key = String(id);
        if (allowedUserIds.size > 0 && !allowedUserIds.has(key)) return;
        if (!map.has(key)) map.set(key, task?.assignedToName || "Assigned user");
      });
    });

    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks, users]);

  const enrichedTasks = useMemo(() => {
    const now = new Date();

    return (tasks || []).map((task) => {
      const due = toDate(task?.dueDate || task?.dueAt);
      const statusKey = String(task?.status || "").trim().toLowerCase();
      const isDone = doneStatuses.has(statusKey);
      const assignedIds = Array.from(
        new Set(
          [
            ...(Array.isArray(task?.assignedToUserIds) ? task.assignedToUserIds : []),
            ...(Array.isArray(task?.memberIds) ? task.memberIds : []),
            task?.assignedUserId,
            task?.assignedToId,
            task?.assignedTo,
          ]
            .filter(Boolean)
            .map((id) => String(id))
        )
      );
      const assigneeId = assignedIds[0] || "";
      const assigneeName =
        assigneeOptions.find((option) => option.id === assigneeId)?.label ||
        task?.assignedToName ||
        "Unassigned";
      const diffHours = due ? (due.getTime() - now.getTime()) / 3600000 : Number.POSITIVE_INFINITY;
      const isOverdue = !isDone && due && due.getTime() < now.getTime();
      const dueSoon = !isDone && due && !isOverdue && diffHours <= 48;

      let urgencyRank = 3;
      if (isOverdue) urgencyRank = 0;
      else if (dueSoon) urgencyRank = 1;
      else if (!isDone) urgencyRank = 2;
      else urgencyRank = 4;

      return {
        ...task,
        assignedIds,
        assigneeId,
        assigneeName,
        creatorIds: getCreatorIds(task),
        due,
        isDone,
        isOverdue,
        dueSoon,
        urgencyRank,
      };
    });
  }, [assigneeOptions, tasks]);

  const visibleTaskSource = useMemo(() => {
    const activeTasks = enrichedTasks.filter((task) => !task.isDone);
    if (isAdmin) return activeTasks;

    return activeTasks.filter(
      (task) =>
        task.creatorIds.some((id) => myIds.includes(id)) ||
        task.assignedIds.some((id) => myIds.includes(id))
    );
  }, [enrichedTasks, isAdmin, myIds]);

  const filteredTasks = useMemo(() => {
    return visibleTaskSource
      .filter((task) => (selectedAssignee === "all" ? true : task.assigneeId === selectedAssignee))
      .filter((task) => {
        if (selectedTaskFilter === "overdue") return task.isOverdue;
        if (selectedTaskFilter === "dueSoon") return task.dueSoon;
        return !task.isDone;
      })
      .sort((a, b) => {
        if (a.urgencyRank !== b.urgencyRank) return a.urgencyRank - b.urgencyRank;
        const aTime = a.due ? a.due.getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.due ? b.due.getTime() : Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) return aTime - bTime;
        return String(a.title || "").localeCompare(String(b.title || ""));
      });
  }, [selectedAssignee, selectedTaskFilter, visibleTaskSource]);

  const stats = useMemo(() => {
    const source = visibleTaskSource.filter((task) =>
      selectedAssignee === "all" ? true : task.assigneeId === selectedAssignee
    );

    return {
      total: source.length,
      assignedToMe: source.filter((task) => task.assignedIds.some((id) => myIds.includes(id))).length,
      createdByMe: source.filter((task) => task.creatorIds.some((id) => myIds.includes(id))).length,
      overdue: source.filter((task) => task.isOverdue).length,
      dueSoon: source.filter((task) => task.dueSoon).length,
      open: source.filter((task) => !task.isDone).length,
    };
  }, [myIds, selectedAssignee, visibleTaskSource]);

  const visibleTasks = filteredTasks.slice(0, 8);

  const tileClassName = (tone, active) =>
    [
      "rounded-xl border px-3 py-2 text-left transition-all",
      active ? "ring-2 ring-offset-1 shadow-sm" : "hover:shadow-sm",
      tone,
    ].join(" ");

  const handleTaskFilterSelect = (filterKey) => {
    setSelectedTaskFilter((prev) => (prev === filterKey ? "open" : filterKey));
  };

  const handleOpenTask = (task) => {
    if (!task?.id) return;
    if (typeof window !== "undefined") {
      try {
        window.sessionStorage.setItem("supplyhawk:pendingTaskModalId", task.id);
      } catch {}
    }
    navigate(`/tasks?taskId=${encodeURIComponent(task.id)}`, { state: { openTaskId: task.id } });
  };

  const handleNotify = async (task) => {
    if (!task?.assigneeId || !companyId) return;
    setNotifyTaskId(task.id);
    try {
      await notifyTaskReminder({
        companyId,
        taskId: task.id,
        taskTitle: task.title || "Task",
        assignedUserId: task.assigneeId,
        remindedByName: me?.fullName || me?.displayName || me?.email || "Someone",
        dueDateLabel: task.due ? `due ${formatDueDate(task.due)}` : "",
      });
    } catch (error) {
      console.error("[TasksSummary] notify reminder failed:", error);
    } finally {
      setNotifyTaskId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-4">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-1">
      <div className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Task Snapshot</h3>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {isAdmin ? "All company tasks, sorted by urgency" : "Tasks you created or are assigned to"}
            </div>
          </div>
          {isAdmin ? (
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700"
            >
              <option value="all">All assignees</option>
              {assigneeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {stats.total} total
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className={`grid gap-2 ${isAdmin ? "grid-cols-3" : "grid-cols-5"}`}>
          {!isAdmin ? (
            <>
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">Assigned</div>
                <div className="mt-0.5 text-lg font-bold text-slate-900">{stats.assignedToMe}</div>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Created</div>
                <div className="mt-0.5 text-lg font-bold text-slate-900">{stats.createdByMe}</div>
              </div>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => handleTaskFilterSelect("overdue")}
            className={tileClassName(
              selectedTaskFilter === "overdue"
                ? "border-red-300 bg-red-100/90 ring-red-300"
                : "border-red-100 bg-red-50/60",
              selectedTaskFilter === "overdue"
            )}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-red-700">Overdue</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900">{stats.overdue}</div>
          </button>
          <button
            type="button"
            onClick={() => handleTaskFilterSelect("dueSoon")}
            className={tileClassName(
              selectedTaskFilter === "dueSoon"
                ? "border-amber-300 bg-amber-100/90 ring-amber-300"
                : "border-amber-100 bg-amber-50/60",
              selectedTaskFilter === "dueSoon"
            )}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Due Soon</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900">{stats.dueSoon}</div>
          </button>
          <button
            type="button"
            onClick={() => handleTaskFilterSelect("open")}
            className={tileClassName(
              selectedTaskFilter === "open"
                ? "border-blue-300 bg-blue-100/90 ring-blue-300"
                : "border-blue-100 bg-blue-50/60",
              selectedTaskFilter === "open"
            )}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">Open</div>
            <div className="mt-0.5 text-lg font-bold text-slate-900">{stats.open}</div>
          </button>
        </div>

        <div className="space-y-1.5">
          {visibleTasks.length === 0 ? (
            <div className="rounded-xl border border-slate-200 px-3 py-5 text-center text-sm text-slate-500">
              No tasks match this view yet.
            </div>
          ) : (
            visibleTasks.map((task) => {
              const createdByMe = task.creatorIds.some((id) => myIds.includes(id));
              const rowTone = task.isOverdue
                ? "text-red-600"
                : task.dueSoon
                ? "text-amber-600"
                : "text-blue-600";
              const metaTone = task.isOverdue
                ? "text-red-700"
                : task.dueSoon
                ? "text-amber-700"
                : "text-slate-500";

              return (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => handleOpenTask(task)}
                  className="w-full rounded-xl px-2 py-2 text-left transition hover:bg-slate-50"
                >
                  <div className="flex items-center gap-2.5">
                    <MapPin className={`h-4 w-4 shrink-0 ${rowTone}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2 text-sm">
                        <span className="truncate font-medium text-slate-900">
                          {task.title || "Untitled task"}
                        </span>
                        {!isAdmin && createdByMe ? (
                          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-violet-600">
                            Mine
                          </span>
                        ) : null}
                      </div>
                      <div className={`mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] ${metaTone}`}>
                        <span>{task.assigneeName}</span>
                        <span className="text-slate-300">•</span>
                        <span>{formatDueDate(task.due)}</span>
                        <span className="text-slate-300">•</span>
                        <span>{task.status || "Open"}</span>
                        {task.isOverdue ? (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="inline-flex items-center gap-1 font-semibold text-red-700">
                              <AlertTriangle className="h-3 w-3" />
                              Overdue
                            </span>
                          </>
                        ) : task.dueSoon ? (
                          <>
                            <span className="text-slate-300">•</span>
                            <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                              <Clock3 className="h-3 w-3" />
                              Due soon
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {isAdmin && task.assigneeId ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleNotify(task);
                        }}
                        disabled={notifyTaskId === task.id}
                        aria-label="Notify assignee"
                        title="Notify assignee"
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-60"
                      >
                        <Bell className={`h-3.5 w-3.5 ${notifyTaskId === task.id ? "animate-pulse" : ""}`} />
                      </button>
                    ) : null}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default TasksSummary;
